/**
 * LA TRÉSORERIE — plan sur 13 semaines et réalisé (lot 100, 18/09/2026).
 *
 * Cadrage : « Analyse financière : 4. la trésorerie » et « 8. les
 * arbitrages », maquette `maquette-analyse-tresorerie.html`. Pur : reçoit
 * les entrées du BFR (mêmes pièces, mêmes ordres, mêmes prévisions) et rend
 * les FLUX à venir, chacun avec sa date probable, sa date prudente, son
 * origine et son calcul. Le plan se refait à l'écran à partir des flux ; la
 * somme d'une case est la somme des flux qu'elle énumère.
 *
 * LES MONTANTS SONT EN CENTIMES, signés : + entrée, − sortie.
 *
 * LES RÈGLES, et d'où elles viennent :
 *   - solde de départ : somme de tous les comptes lus (doc 4) ;
 *   - dates : celles de l'échéancier, une date dépassée compte aujourd'hui
 *     (doc 8) ;
 *   - PRUDENT = seulement le retard des clients : pire retard constaté au
 *     lieu du retard habituel, pour les dates CALCULÉES ; les sorties restent
 *     à leur date (doc 4, validé le 17/09) ;
 *   - facture cédée : le financement compte À LA CESSION (TTC − retenue), la
 *     retenue est rendue quand le client paie (doc 4) ;
 *   - TVA : collectée − déductible du mois, payée le jour réglé du mois
 *     suivant — mêmes pièces que le BFR ; un crédit n'est pas un flux ;
 *   - affrètement : compté dès la planification, payé
 *     `delai_paiement_affretement` jours après la facture attendue (doc 4) ;
 *   - mouvements bancaires rangés par règles sur le libellé, modifiables
 *     (doc 4).
 *
 * SUPPOSÉ (non tranché au cadrage, dit à l'écran) :
 *   - un transport à facturer d'un client dont la DERNIÈRE facture a été
 *     cédée sera cédé aussi, `delai_cession` jours après sa facture ;
 *   - la commission d'affacturage est un taux du TTC cédé du mois, TVA
 *     comprise, prélevée en fin de mois ; 0 tant qu'il n'est pas saisi ;
 *   - les charges sociales du réglage sont payées le jour réglé ; une
 *     prévision « URSSAF » les compterait une seconde fois.
 */

import {
  finDeMois,
  moisSuivant,
  projectionOrdre,
  type EntreesBfr,
  type Semaine,
} from './bfr.js';
import {
  ajouterJours,
  ecartJours,
  mediane,
  type ReglementPasse,
} from './echeancier.js';

export type Nature =
  'clients' | 'factor' | 'fournisseurs' | 'flotte' | 'personnel' | 'tva' | 'autres';

export const NATURES: readonly { readonly cle: Nature; readonly nom: string }[] = [
  { cle: 'clients', nom: 'Encaissements clients' },
  { cle: 'factor', nom: 'Factor : financements et retenues rendues' },
  { cle: 'fournisseurs', nom: 'Fournisseurs et sous-traitants' },
  { cle: 'flotte', nom: 'Flotte : LOA, carburant, péages' },
  { cle: 'personnel', nom: 'Salaires et charges sociales' },
  { cle: 'tva', nom: 'TVA à reverser' },
  { cle: 'autres', nom: 'Autres (bureau, assurance, taxes, commission factor)' },
];

export const EST_NATURE = (v: unknown): v is Nature =>
  typeof v === 'string' && NATURES.some((n) => n.cle === v);

export interface ReglagesTresorerie {
  /** Taux en centièmes de point : 10 % = 1000. */
  readonly retenue_factor: number;
  readonly commission_factor: number;
  readonly delai_cession: number;
  /** Centimes. */
  readonly minimum: number;
  readonly decouvert: number;
}

export interface Retards {
  /** Retard habituel (médiane), en jours. */
  readonly habituel: number;
  /** Pire retard constaté, en jours. */
  readonly pire: number;
}

export interface EntreesTresorerie {
  readonly bfr: EntreesBfr;
  readonly reglages: ReglagesTresorerie;
  /** Par tiers client (identifiant Pennylane). */
  readonly retards: ReadonlyMap<string, Retards>;
  /** Tiers dont la dernière facture a été cédée. */
  readonly affactures: ReadonlySet<string>;
  /** Règles de l'utilisateur, avant les règles de base. */
  readonly regles: readonly RegleNature[];
  /** Dernier jour du plan. */
  readonly horizon: string;
}

export interface FluxTresorerie {
  readonly cle: string;
  readonly nature: Nature;
  /** Date probable. */
  readonly date: string;
  /** Date prudente — jamais avant la probable. */
  readonly prudent: string;
  readonly montant: number;
  readonly libelle: string;
  readonly tiers: string;
  readonly source: string;
  readonly calcul: string;
  readonly a_revoir: boolean;
}

/**
 * LE RETARD HABITUEL ET LE PIRE RETARD d'un tiers, sur les mêmes règlements
 * que l'échéancier (dates manquantes ou incohérentes écartées). Nul sans
 * règlement retenu.
 */
export function retardsExtremes(reglements: readonly ReglementPasse[]): Retards | null {
  const ecarts: number[] = [];
  for (const r of reglements) {
    if (r.regle_le === null || r.echeance === null) continue;
    if (r.date_piece !== null && r.regle_le < r.date_piece) continue;
    ecarts.push(ecartJours(r.echeance, r.regle_le));
  }
  const m = mediane(ecarts);
  if (m === null) return null;
  return { habituel: Math.round(m), pire: Math.max(...ecarts) };
}

// ---------------------------------------------------------------------------
// Les règles de rangement
// ---------------------------------------------------------------------------

export interface RegleNature {
  readonly id: string;
  /** Mot ou suite de mots cherchés dans le libellé, sans casse ni accent. */
  readonly motif: string;
  readonly nature: Nature;
}

/**
 * LES RÈGLES DE BASE, dans l'ordre. « COMMISSION » passe avant « FACTOR »
 * (la commission du factor est une charge, pas un financement), « ASSURANCE »
 * avant la flotte (l'assurance flotte est rangée en autres, comme dans la
 * maquette).
 */
export const REGLES_DE_BASE: readonly RegleNature[] = [
  ['COMMISSION', 'autres'],
  ['ASSURANCE', 'autres'],
  ['FACTOR', 'factor'],
  ['AFFACTURAGE', 'factor'],
  ['FACTORING', 'factor'],
  ['TVA', 'tva'],
  ['URSSAF', 'personnel'],
  ['SALAIRE', 'personnel'],
  ['SALAIRES', 'personnel'],
  ['PAIE', 'personnel'],
  ['RETRAITE', 'personnel'],
  ['PREVOYANCE', 'personnel'],
  ['MUTUELLE', 'personnel'],
  ['CARBURANT', 'flotte'],
  ['GASOIL', 'flotte'],
  ['GAZOLE', 'flotte'],
  ['PEAGE', 'flotte'],
  ['TELEPEAGE', 'flotte'],
  ['LOA', 'flotte'],
  ['CREDIT BAIL', 'flotte'],
  ['PNEU', 'flotte'],
  ['PNEUS', 'flotte'],
  ['LOYER', 'autres'],
  ['FRAIS BANCAIRES', 'autres'],
  ['IMPOT', 'autres'],
  ['TAXE', 'autres'],
].map(([motif, nature], i) => ({
  id: `base-${String(i + 1)}`,
  motif: motif as string,
  nature: nature as Nature,
}));

/** Majuscules, sans accents, ponctuation ramenée à des espaces. */
export function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

/** Le motif est-il dans le texte, en mots entiers ? */
export function contientMotif(texte: string, motif: string): boolean {
  const m = normaliser(motif);
  if (m === '') return false;
  return ` ${normaliser(texte)} `.includes(` ${m} `);
}

export interface Rangement {
  readonly nature: Nature;
  /** La règle appliquée, ou nul pour la nature par défaut. */
  readonly regle: RegleNature | null;
}

export function ranger(
  texte: string,
  regles: readonly RegleNature[],
  parDefaut: Nature,
): Rangement {
  for (const r of [...regles, ...REGLES_DE_BASE]) {
    if (contientMotif(texte, r.motif)) return { nature: r.nature, regle: r };
  }
  return { nature: parDefaut, regle: null };
}

/** Un mouvement bancaire : client → clients, fournisseur → fournisseurs, sinon selon le signe. */
export function rangerMouvement(
  m: {
    readonly libelle: string | null;
    readonly client: boolean;
    readonly fournisseur: boolean;
    readonly montant: number;
  },
  regles: readonly RegleNature[],
): Rangement {
  const defaut: Nature = m.client
    ? 'clients'
    : m.fournisseur
      ? 'fournisseurs'
      : m.montant > 0
        ? 'clients'
        : 'autres';
  return ranger(m.libelle ?? '', regles, defaut);
}

// ---------------------------------------------------------------------------
// Les flux à venir
// ---------------------------------------------------------------------------

const somme = <T>(l: readonly T[], f: (x: T) => number) =>
  l.reduce((s, x) => s + f(x), 0);

const auPlusTot = (d: string, auj: string) => (d < auj ? auj : d);
const auPlusTard = (a: string, b: string) => (a > b ? a : b);
const part = (montant: number, taux: number) => Math.round((montant * taux) / 10000);
const pct = (taux: number) => `${String(taux / 100).replace('.', ',')} %`;
const fr = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const eur = (c: number) =>
  `${(c / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export interface TvaDuMois {
  readonly mois: string;
  readonly paiement: string;
  readonly lignes: readonly {
    libelle: string;
    collectee: number;
    deductible: number;
  }[];
  readonly collectee: number;
  readonly deductible: number;
  readonly net: number;
}

/**
 * LA TVA D'UN MOIS : mêmes pièces que le bloc TVA du BFR — pièces Pennylane
 * datées du mois, lignes prévues et transports à facturer à venir — plus la
 * TVA de la commission d'affacturage du mois.
 */
export function tvaDuMois(e: EntreesTresorerie, mois: string): TvaDuMois {
  const b = e.bfr;
  const auj = b.aujourdhui;
  const dans = (x: string) => x.startsWith(mois);
  const lignes: { libelle: string; collectee: number; deductible: number }[] = [];
  // SANS HORS-TAXE LISIBLE, LA PIÈCE EST ÉCARTÉE (lot 113), comme dans le bloc
  // TVA du BFR. Le compte affiché dans le libellé porte donc sur les pièces
  // RETENUES : annoncer « 12 factures » et n'en compter que dix serait la même
  // faute, déplacée dans le texte.
  const pc = b.clients.filter((p) => dans(p.date) && p.ht !== null);
  lignes.push({
    libelle: `TVA collectée sur ${String(pc.length)} facture(s) clients Pennylane`,
    collectee: somme(pc, (p) => p.ttc - (p.ht as number)),
    deductible: 0,
  });
  const pf = b.fournisseurs.filter((p) => dans(p.date) && p.ht !== null);
  lignes.push({
    libelle: `TVA déductible sur ${String(pf.length)} facture(s) fournisseurs Pennylane`,
    collectee: 0,
    deductible: somme(pf, (p) => p.ttc - (p.ht as number)),
  });
  const lc = b.lignes.filter(
    (l) => dans(l.date) && l.date >= auj && l.cote === 'client',
  );
  const lf = b.lignes.filter(
    (l) => dans(l.date) && l.date >= auj && l.cote === 'fournisseur',
  );
  if (lc.length > 0 || lf.length > 0) {
    lignes.push({
      libelle: `Prévisions : ${String(lc.length)} ligne(s) clients, ${String(lf.length)} ligne(s) charges`,
      collectee: somme(lc, (l) => l.ttc - l.ht),
      deductible: somme(lf, (l) => l.ttc - l.ht),
    });
  }
  const ot = b.ordres.filter((o) => {
    if (o.annule || o.facture_le !== null || b.tiersRemplaces.has(o.tiers))
      return false;
    const pr = projectionOrdre(o, b);
    return dans(pr.facture) && pr.facture > auj;
  });
  if (ot.length > 0) {
    lignes.push({
      libelle: `TVA collectée sur ${String(ot.length)} transport(s) à facturer`,
      collectee: somme(ot, (o) => o.vente_ttc - o.vente_ht),
      deductible: 0,
    });
  }
  const com = commissionDuMois(e, mois);
  if (com.ttc > 0) {
    lignes.push({
      libelle: 'TVA déductible sur la commission d’affacturage',
      collectee: 0,
      deductible: com.ttc - Math.round(com.ttc / 1.2),
    });
  }
  const collectee = somme(lignes, (l) => l.collectee);
  const deductible = somme(lignes, (l) => l.deductible);
  return {
    mois,
    paiement: moisSuivant(`${mois}-01`, b.reglages.jour_tva),
    lignes,
    collectee,
    deductible,
    net: collectee - deductible,
  };
}

interface Cession {
  readonly cle: string;
  readonly numero: string;
  readonly tiers: string;
  readonly ttc: number;
  readonly cession: string;
}

/** Les cessions (faites ou supposées) : pièces cédées et transports à céder. */
function cessions(e: EntreesTresorerie): Cession[] {
  const b = e.bfr;
  const res: Cession[] = [];
  for (const p of b.clients) {
    if (p.cedee_le === null) continue;
    res.push({
      cle: p.cle,
      numero: p.numero ?? p.cle,
      tiers: p.tiers_nom,
      ttc: p.ttc,
      cession: p.cedee_le,
    });
  }
  for (const o of b.ordres) {
    if (!ordreAProjeter(e, o) || !e.affactures.has(o.tiers)) continue;
    const pr = projectionOrdre(o, b);
    res.push({
      cle: `ot:${o.id}`,
      numero: o.numero,
      tiers: o.tiers_nom,
      ttc: o.vente_ttc,
      cession: auPlusTot(
        ajouterJours(pr.facture, e.reglages.delai_cession),
        b.aujourdhui,
      ),
    });
  }
  return res;
}

function commissionDuMois(e: EntreesTresorerie, mois: string) {
  const liste = cessions(e).filter((c) => c.cession.startsWith(mois));
  const base = somme(liste, (c) => c.ttc);
  return {
    liste,
    base,
    ttc: Math.round(part(base, e.reglages.commission_factor) * 1.2),
  };
}

function ordreAProjeter(
  e: EntreesTresorerie,
  o: EntreesBfr['ordres'][number],
): boolean {
  const b = e.bfr;
  if (o.annule || o.facture_le !== null) return false;
  // Même règle que le BFR : un ordre planifié d'un client sous contrat est
  // remplacé par les lignes du contrat ; un ordre déjà livré reste.
  return !(o.livre_le > b.aujourdhui && b.tiersRemplaces.has(o.tiers));
}

/** Les mois dont un paiement (TVA, charges) peut tomber dans le plan. */
function moisDuPlan(auj: string, horizon: string): string[] {
  const res: string[] = [];
  let m = ajouterJours(`${auj.slice(0, 8)}01`, -1).slice(0, 7);
  while (`${m}-01` <= horizon) {
    res.push(m);
    m = ajouterJours(finDeMois(`${m}-01`), 1).slice(0, 7);
  }
  return res;
}

export function fluxAVenir(e: EntreesTresorerie): FluxTresorerie[] {
  const b = e.bfr;
  const auj = b.aujourdhui;
  const r = e.reglages;
  const res: FluxTresorerie[] = [];
  const ajouter = (f: FluxTresorerie) => {
    // Un flux au-delà de l'horizon n'entre pas ; son double prudent, plus
    // tardif encore, non plus.
    if (f.montant !== 0 && f.date >= auj && f.date <= e.horizon) res.push(f);
  };
  const prudentDe = (
    tiers: string,
    base: string,
    probable: string,
    calculee: boolean,
  ): { date: string; note: string } => {
    const rt = e.retards.get(tiers);
    if (!calculee || rt === undefined) return { date: probable, note: '' };
    const d = auPlusTard(
      auPlusTot(ajouterJours(base, Math.max(0, rt.pire)), auj),
      probable,
    );
    return {
      date: d,
      note: ` · prudent : ${fr(base)} + pire retard ${String(Math.max(0, rt.pire))} j`,
    };
  };

  // 1. Pièces clients ouvertes.
  for (const p of b.clients) {
    if (p.reste <= 0) continue;
    const base = p.echeance ?? p.date;
    const n = p.parts.length;
    p.parts.forEach((x, i) => {
      const calculee = x.origine === undefined || x.origine === 'calculee';
      const pr = prudentDe(p.tiers, base, x.date, calculee);
      const rang = n > 1 ? ` · paiement ${String(i + 1)}/${String(n)}` : '';
      const origine =
        x.origine === 'saisie' || x.origine === 'echeancier'
          ? `date saisie dans l’échéancier : ${fr(x.date)}`
          : x.origine === 'prelevement'
            ? `prélèvement à l’échéance ${fr(base)}`
            : `échéance ${fr(base)}`;
      const revoir = x.a_revoir === true ? ' → dépassé, compté aujourd’hui' : '';
      if (p.cedee_le !== null) {
        const retenue = part(x.montant, r.retenue_factor);
        ajouter({
          cle: `ret:${p.cle}:${String(i)}`,
          nature: 'factor',
          date: x.date,
          prudent: pr.date,
          montant: retenue,
          libelle: `Retenue rendue · ${p.numero ?? p.cle}${rang}`,
          tiers: p.tiers_nom,
          source: 'Factor, au paiement du client',
          calcul: `${pct(r.retenue_factor)} de ${eur(x.montant)}, rendue quand le client paie : ${origine}${revoir}${pr.note}`,
          a_revoir: x.a_revoir === true,
        });
        return;
      }
      ajouter({
        cle: `${p.cle}:${String(i)}`,
        nature: 'clients',
        date: x.date,
        prudent: pr.date,
        montant: x.montant,
        libelle: `${p.numero ?? p.cle}${rang}`,
        tiers: p.tiers_nom,
        source: 'Échéancier (Pennylane)',
        calcul: `${origine}${revoir}${pr.note}`,
        a_revoir: x.a_revoir === true,
      });
    });
    // Financement d'une cession à venir.
    if (p.cedee_le !== null && p.cedee_le >= auj) {
      const net = p.ttc - part(p.ttc, r.retenue_factor);
      ajouter({
        cle: `fin:${p.cle}`,
        nature: 'factor',
        date: p.cedee_le,
        prudent: p.cedee_le,
        montant: net,
        libelle: `Financement factor · ${p.numero ?? p.cle}`,
        tiers: p.tiers_nom,
        source: 'Facture cédée (Stafa)',
        calcul: `${eur(p.ttc)} TTC − retenue ${pct(r.retenue_factor)} ; cession le ${fr(p.cedee_le)}`,
        a_revoir: false,
      });
    }
  }

  // 2. Transports à facturer.
  for (const o of b.ordres) {
    if (!ordreAProjeter(e, o)) continue;
    const pr = projectionOrdre(o, b);
    const echeance = ajouterJours(pr.facture, b.reglages.delai_client);
    const prud = prudentDe(o.tiers, echeance, pr.reglement, true);
    const calcul = `facture supposée le ${fr(pr.facture)} (fin du mois de livraison), + ${String(b.reglages.delai_client)} j + retard habituel ${String(Math.max(0, o.retard_client))} j${prud.note}`;
    if (e.affactures.has(o.tiers)) {
      const cession = auPlusTot(ajouterJours(pr.facture, r.delai_cession), auj);
      const retenue = part(o.vente_ttc, r.retenue_factor);
      ajouter({
        cle: `fin:ot:${o.id}`,
        nature: 'factor',
        date: cession,
        prudent: cession,
        montant: o.vente_ttc - retenue,
        libelle: `Financement factor · ${o.numero} (à facturer)`,
        tiers: o.tiers_nom,
        source: 'Transport Stafa, facture à céder (supposé : client affacturé)',
        calcul: `${eur(o.vente_ttc)} TTC − retenue ${pct(r.retenue_factor)} ; cession le ${fr(pr.facture)} + ${String(r.delai_cession)} j`,
        a_revoir: false,
      });
      ajouter({
        cle: `ret:ot:${o.id}`,
        nature: 'factor',
        date: pr.reglement,
        prudent: prud.date,
        montant: retenue,
        libelle: `Retenue rendue · ${o.numero} (à facturer)`,
        tiers: o.tiers_nom,
        source: 'Factor, au paiement du client',
        calcul,
        a_revoir: false,
      });
    } else {
      ajouter({
        cle: `ot:${o.id}`,
        nature: 'clients',
        date: pr.reglement,
        prudent: prud.date,
        montant: o.vente_ttc,
        libelle: `${o.numero} (à facturer)`,
        tiers: o.tiers_nom,
        source: 'Transport Stafa',
        calcul,
        a_revoir: false,
      });
    }
  }

  // 3. Pièces fournisseurs ouvertes.
  for (const p of b.fournisseurs) {
    if (p.reste <= 0) continue;
    const nature = ranger(p.tiers_nom, e.regles, 'fournisseurs').nature;
    const n = p.parts.length;
    p.parts.forEach((x, i) => {
      ajouter({
        cle: `${p.cle}:${String(i)}`,
        nature,
        date: x.date,
        prudent: x.date,
        montant: -x.montant,
        libelle: `${p.numero ?? p.cle}${n > 1 ? ` · paiement ${String(i + 1)}/${String(n)}` : ''}`,
        tiers: p.tiers_nom,
        source: 'Échéancier (Pennylane)',
        calcul:
          x.origine === 'prelevement'
            ? `prélèvement à l’échéance${x.a_revoir === true ? ' → dépassé, compté aujourd’hui' : ''}`
            : x.origine === 'calculee' || x.origine === undefined
              ? `échéance ${fr(p.echeance ?? p.date)} + délai habituel${x.a_revoir === true ? ' → dépassé, compté aujourd’hui' : ''}`
              : `date saisie dans l’échéancier : ${fr(x.date)}`,
        a_revoir: x.a_revoir === true,
      });
    });
  }

  // 4. Affrètements dont la facture n'est pas encore reçue.
  for (const o of b.ordres) {
    if (o.annule || o.achat_ht === null || o.achat_ht <= 0) continue;
    const recue = ajouterJours(o.livre_le, b.reglages.delai_facture_affretement);
    if (recue <= auj) continue; // déjà dans Pennylane
    const payee = ajouterJours(recue, b.reglages.delai_paiement_affretement);
    ajouter({
      cle: `aff:${o.id}`,
      nature: 'fournisseurs',
      date: payee,
      prudent: payee,
      montant: -Math.round(o.achat_ht * 1.2),
      libelle: `Affrètement ${o.numero} (facture attendue le ${fr(recue)})`,
      tiers: o.tiers_nom,
      source: 'Affrètement planifié (Stafa)',
      calcul: `livraison ${fr(o.livre_le)} + ${String(b.reglages.delai_facture_affretement)} j (facture) + ${String(b.reglages.delai_paiement_affretement)} j (paiement), ${eur(o.achat_ht)} HT + TVA 20 %`,
      a_revoir: false,
    });
  }

  // 5. Prévisions.
  for (const l of b.lignes) {
    if (l.reglement < auj) continue;
    const client = l.cote === 'client';
    const rt = client ? e.retards.get(l.tiers) : undefined;
    const prudent =
      rt === undefined
        ? l.reglement
        : ajouterJours(l.reglement, Math.max(0, rt.pire) - Math.max(0, rt.habituel));
    ajouter({
      cle: `prev:${l.cle}`,
      nature: client
        ? 'clients'
        : ranger(
            `${l.libelle} ${l.tiers_nom}`,
            e.regles,
            l.tiers.startsWith('f:prev:') ? 'autres' : 'fournisseurs',
          ).nature,
      date: l.reglement,
      prudent: auPlusTard(prudent, l.reglement),
      montant: client ? l.ttc : -l.ttc,
      libelle: l.libelle,
      tiers: l.tiers_nom,
      source: 'Prévisions',
      calcul: `pièce attendue le ${fr(l.date)}, réglée le ${fr(l.reglement)}${prudent > l.reglement ? ` · prudent : + ${String(Math.max(0, (rt?.pire ?? 0) - Math.max(0, rt?.habituel ?? 0)))} j` : ''}`,
      a_revoir: false,
    });
  }

  // 6. Charges sociales (réglage), TVA, commission d'affacturage.
  for (const mois of moisDuPlan(auj, e.horizon)) {
    const M = b.reglages.charges_sociales;
    if (M > 0) {
      const paie = moisSuivant(`${mois}-01`, b.reglages.jour_charges_sociales);
      ajouter({
        cle: `soc:${mois}`,
        nature: 'personnel',
        date: paie,
        prudent: paie,
        montant: -M,
        libelle: `Charges sociales de ${mois.slice(5)}/${mois.slice(0, 4)}`,
        tiers: 'Organismes sociaux',
        source: 'Réglages de l’analyse',
        calcul: `montant du réglage, payé le ${String(b.reglages.jour_charges_sociales)} du mois suivant`,
        a_revoir: false,
      });
    }
    const t = tvaDuMois(e, mois);
    if (t.net > 0) {
      ajouter({
        cle: `tva:${mois}`,
        nature: 'tva',
        date: t.paiement,
        prudent: t.paiement,
        montant: -t.net,
        libelle: `TVA de ${mois.slice(5)}/${mois.slice(0, 4)}`,
        tiers: 'Trésor public',
        source: 'Calcul Stafa',
        calcul: `collectée ${eur(t.collectee)} − déductible ${eur(t.deductible)}, payée le ${fr(t.paiement)}`,
        a_revoir: false,
      });
    }
    const com = commissionDuMois(e, mois);
    if (com.ttc > 0) {
      const d = finDeMois(`${mois}-01`);
      ajouter({
        cle: `com:${mois}`,
        nature: 'autres',
        date: d,
        prudent: d,
        montant: -com.ttc,
        libelle: `Commission d’affacturage ${mois.slice(5)}/${mois.slice(0, 4)}`,
        tiers: 'Factor',
        source: 'Calcul Stafa',
        calcul: `${pct(r.commission_factor)} de ${eur(com.base)} cédés (${String(com.liste.length)} facture(s)), TVA comprise`,
        a_revoir: false,
      });
    }
  }
  return res;
}

// ---------------------------------------------------------------------------
// Le plan
// ---------------------------------------------------------------------------

export type Scenario = 'probable' | 'prudent';

export const dateDuFlux = (f: FluxTresorerie, sc: Scenario) =>
  sc === 'prudent' ? f.prudent : f.date;

export interface SemainePlan {
  readonly semaine: Semaine;
  readonly debut: number;
  readonly parNature: Readonly<Record<Nature, number>>;
  readonly entrees: number;
  readonly sorties: number;
  readonly variation: number;
  readonly fin: number;
}

/** Les flux d'une semaine du plan (la courante commence aujourd'hui). */
export function fluxDeLaSemaine(
  flux: readonly FluxTresorerie[],
  s: Semaine,
  sc: Scenario,
  aujourdhui: string,
): FluxTresorerie[] {
  const debut = s.lundi < aujourdhui ? aujourdhui : s.lundi;
  return flux.filter((f) => {
    const d = dateDuFlux(f, sc);
    return d >= debut && d <= s.dimanche;
  });
}

export function planTresorerie(
  solde: number,
  flux: readonly FluxTresorerie[],
  semaines: readonly Semaine[],
  sc: Scenario,
  aujourdhui: string,
): SemainePlan[] {
  let courant = solde;
  return semaines.map((s) => {
    const dans = fluxDeLaSemaine(flux, s, sc, aujourdhui);
    const parNature = Object.fromEntries(
      NATURES.map((n) => [
        n.cle,
        somme(
          dans.filter((f) => f.nature === n.cle),
          (f) => f.montant,
        ),
      ]),
    ) as Record<Nature, number>;
    const entrees = somme(
      dans.filter((f) => f.montant > 0),
      (f) => f.montant,
    );
    const sorties = somme(
      dans.filter((f) => f.montant < 0),
      (f) => f.montant,
    );
    const debut = courant;
    courant += entrees + sorties;
    return {
      semaine: s,
      debut,
      parNature,
      entrees,
      sorties,
      variation: entrees + sorties,
      fin: courant,
    };
  });
}

export function pointBas(plan: readonly SemainePlan[]): SemainePlan | null {
  return plan.reduce<SemainePlan | null>(
    (a, x) => (a === null || x.fin < a.fin ? x : a),
    null,
  );
}

export type Niveau = 'vert' | 'orange' | 'rouge';

export function niveau(v: number, r: ReglagesTresorerie): Niveau {
  if (v < -r.decouvert) return 'rouge';
  if (v < r.minimum) return 'orange';
  return 'vert';
}

// ---------------------------------------------------------------------------
// Le passé
// ---------------------------------------------------------------------------

export interface MouvementRange {
  readonly cle: string;
  readonly date: string;
  readonly libelle: string;
  readonly compte: string;
  readonly montant: number;
  readonly nature: Nature;
  readonly regle: string | null;
}

export interface SemainePassee {
  readonly semaine: Semaine;
  readonly debut: number;
  readonly fin: number;
  readonly variation: number;
  readonly parNature: Readonly<Record<Nature, number>>;
  readonly mouvements: readonly MouvementRange[];
}

/**
 * LES SEMAINES PASSÉES, reconstruites à REBOURS depuis le solde du jour : le
 * solde de fin d'une semaine est le solde du jour moins les mouvements
 * postérieurs. La semaine en cours s'arrête à aujourd'hui.
 */
export function semainesPassees(
  soldeDuJour: number,
  mouvements: readonly MouvementRange[],
  semaines: readonly Semaine[],
  aujourdhui: string,
): SemainePassee[] {
  return semaines.map((s) => {
    const arret = s.dimanche > aujourdhui ? aujourdhui : s.dimanche;
    const apres = somme(
      mouvements.filter((m) => m.date > arret),
      (m) => m.montant,
    );
    const dans = mouvements.filter((m) => m.date >= s.lundi && m.date <= arret);
    const variation = somme(dans, (m) => m.montant);
    const fin = soldeDuJour - apres;
    return {
      semaine: s,
      debut: fin - variation,
      fin,
      variation,
      parNature: Object.fromEntries(
        NATURES.map((n) => [
          n.cle,
          somme(
            dans.filter((m) => m.nature === n.cle),
            (m) => m.montant,
          ),
        ]),
      ) as Record<Nature, number>,
      mouvements: dans,
    };
  });
}

/** La photo du lundi : ce que le plan prévoyait pour la semaine. */
export interface PhotoSemaine {
  readonly lundi: string;
  readonly pris_le: string;
  readonly debut: number;
  readonly variation: number;
  readonly fin: number;
  readonly parNature: Readonly<Record<Nature, number>>;
}

export function photoDe(
  plan: readonly SemainePlan[],
  prisLe: string,
): PhotoSemaine | null {
  const s = plan[0];
  if (s === undefined) return null;
  return {
    lundi: s.semaine.lundi,
    pris_le: prisLe,
    debut: s.debut,
    variation: s.variation,
    fin: s.fin,
    parNature: s.parNature,
  };
}

/**
 * CONTRÔLES de la maquette, repris : chaîne des soldes, somme des natures =
 * variation, fin = départ + somme des flux de la fenêtre, date prudente
 * jamais avant la probable, aucun flux avant aujourd'hui. Rend les écarts.
 */
export function verifierTresorerie(
  solde: number,
  flux: readonly FluxTresorerie[],
  plan: readonly SemainePlan[],
  sc: Scenario,
  aujourdhui: string,
): string[] {
  const fautes: string[] = [];
  let prec = solde;
  for (const p of plan) {
    if (p.debut !== prec)
      fautes.push(`S${String(p.semaine.numero)} : début ≠ fin précédente`);
    if (p.fin !== p.debut + p.variation)
      fautes.push(`S${String(p.semaine.numero)} : fin ≠ début + variation`);
    if (somme(Object.values(p.parNature), (x) => x) !== p.variation) {
      fautes.push(`S${String(p.semaine.numero)} : natures ≠ variation`);
    }
    prec = p.fin;
  }
  const dernier = plan[plan.length - 1];
  if (dernier !== undefined) {
    const total = somme(
      flux.filter((f) => dateDuFlux(f, sc) <= dernier.semaine.dimanche),
      (f) => f.montant,
    );
    if (dernier.fin !== solde + total) fautes.push('fin ≠ départ + flux de la fenêtre');
  }
  for (const f of flux) {
    if (f.prudent < f.date) fautes.push(`${f.cle} : prudent avant probable`);
    if (f.date < aujourdhui) fautes.push(`${f.cle} : flux avant aujourd’hui`);
  }
  return fautes;
}
